import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography, styled } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GroupsIcon from "@mui/icons-material/Groups";
import SpeedIcon from "@mui/icons-material/Speed";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 640,
});

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardHeading = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.93rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
});

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: <AutoAwesomeIcon />,
    title: "AI specialists, not generalists",
    description:
      "In-house practice in NLP, computer vision, and predictive analytics. We build LLM-powered features, not chatbot wrappers.",
  },
  {
    icon: <GroupsIcon />,
    title: "Senior team, no handoff",
    description:
      "The team that scopes your project ships it — same engineers from discovery to deployment, every time.",
  },
  {
    icon: <SpeedIcon />,
    title: "Ship in weeks, not quarters",
    description:
      "MVPs in 6–12 weeks. AI integrations in 2–4 weeks. Milestone-based delivery with working software every sprint.",
  },
  {
    icon: <PublicIcon />,
    title: "Two offices, one standard",
    description:
      "Engineering in Pakistan, partnership in Canada. 10+ hours of daily overlap with North America and Europe.",
  },
  {
    icon: <VerifiedUserIcon />,
    title: "You own everything",
    description:
      "100% IP transfer on final payment. Source code, infrastructure, design files, documentation — all yours, in writing.",
  },
  {
    icon: <SupportAgentIcon />,
    title: "Stay with us post-launch",
    description:
      "Ongoing support, monitoring, and feature work after delivery. We maintain what we build.",
  },
];

const WhyChooseUsSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="why-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#f8fafc",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mb: { xs: 5, md: 7 },
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Label>Why us</Label>
          <SectionTitle id="why-heading" variant="h2">
            What makes us a great technical partner
          </SectionTitle>
          <Lead>
            Six years shipping production software, an in-house AI practice,
            and a senior team that stays with your product from idea to scale.
          </Lead>
        </Box>

        <Grid container spacing={2.5}>
          {FEATURES.map((feature, i) => (
            <Grid key={feature.title} item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  height: "100%",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.45s ease ${
                    0.05 + i * 0.05
                  }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                }}
              >
                <Card>
                  <IconBadge>{feature.icon}</IconBadge>
                  <CardHeading variant="h3">{feature.title}</CardHeading>
                  <CardCopy>{feature.description}</CardCopy>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseUsSection;
